import { TableSkeleton } from "components/PortalLoading";
import AdminPageLoader from "components/AdminPageLoader";
import { portalPageMeta } from "layouts/Admin2/pageMeta";
import PageHeading from "admin/components/PageHeading";
import React from "react";
import Admin2 from "layouts/Admin2";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import usePublisherPurchasedBooks from "api/BookResource/usePublisherPurchasedBooks";
import useExportPublisherPurchasedBooks from "api/BookResource/useExportPublisherPurchasedBooks";
import usePurchasedBooksFilters from "admin/PublisherPurchasedBooks/usePurchasedBooksFilters";
import { deleteEmptyObjects } from "utils";
import { Form, Formik } from "formik";
import Calendar from "components/PortalDatePicker";
import moment from "moment";
import { BsFilterRight } from "react-icons/bs";
import { FiDownload, FiX } from "react-icons/fi";
import { CustomPaginator } from "components";
import TableView from "admin/PublisherPurchasedBooks/TableView";

const PublisherSalesExport = () => {
	const filters = usePurchasedBooksFilters();
	const exportToExcel = useExportPublisherPurchasedBooks();

	const { data: publishersRes, isLoading: isLoadingPublishers } = useFetchPublishers();
	const publishersData = publishersRes?.data;
	const publishers = React.useMemo(() => publishersData ?? [], [publishersData]);

	const [publisherId, setPublisherId] = React.useState("");

	const initialValues = {
		searchTerm: filters.searchTerm,
		categoryIs: filters.categoryIs,
		page: filters.page,
		rowsPerPage: filters.rowsPerPage,
		firstPage: filters.firstPage,
		fromDate: filters.fromDate,
		toDate: filters.toDate,
		revenueLine: filters.revenueLine,
	};

	const handleSubmit = (values) => {
		const params = {
			first_page: values.firstPage,
			rows: values.rowsPerPage,
			page: values.page,
			category_is: values.categoryIs,
			search_term: values.searchTerm,
			from_date: values.fromDate,
			to_date: values.toDate,
			revenue_line: values.revenueLine,
		};
		const queryParams = deleteEmptyObjects(params);
		filters.changeAllFiltersAtOnce(queryParams);
	};

	const resetFiltersToDefaults = React.useCallback(() => {
		const params = {
			first_page: "",
			rows: "50",
			page: "1",
			category_is: "",
			search_term: "",
			from_date: "",
			to_date: "",
			revenue_line: "purchase",
		};
		const queryParams = deleteEmptyObjects(params);
		filters.changeAllFiltersAtOnce(queryParams);
	}, [filters]);

	const { data: books, isLoading: isLoadingBooks } = usePublisherPurchasedBooks(
		{
			page: filters.page,
			rows_per_page: filters.rowsPerPage,
			from_date: filters.fromDate,
			to_date: filters.toDate,
			searchTerm: filters.searchTerm,
			revenue_line: filters.revenueLine,
			category_is: filters.categoryIs,
		},
		publisherId ? String(publisherId) : "",
		{ enabled: Boolean(publisherId) },
	);

	const totalRecords = books?.data?.total_records || 0;

	if (isLoadingPublishers) return <AdminPageLoader />;

	return (
		<Admin2>
			<div className="mt-4 w-full space-y-4 flex flex-col flex-1">
				<div className="bg-white rounded-2xl p-4 md:p-6 shadow-lg space-y-3">
					<PageHeading title="Publisher sales" description={portalPageMeta("/admin/publishers/sold-books-export").description} />
					<div className="flex flex-col md:flex-row gap-3 md:items-end">
						<div className="flex flex-col">
							<label className="text-sm text-gray-600 mb-1">Publisher</label>
							<select
								value={publisherId}
								onChange={(e) => {
									const nextPublisherId = e.target.value;
									setPublisherId(nextPublisherId);
									if (nextPublisherId) {
										resetFiltersToDefaults();
									}
								}}
								className="h-10 px-3 border border-gray-300 rounded-lg bg-white min-w-72"
							>
								<option value="">Select a publisher...</option>
								{publishers
									?.filter((p) => p.name?.trim()?.length > 0)
									.map((p) => (
										<option key={p.id} value={p.id}>
											{p.name}
										</option>
									))}
							</select>
						</div>
					</div>

					{!publisherId && (
						<p className="text-sm text-gray-600">
							Select a publisher to view purchased books, filters, and export options.
						</p>
					)}
				</div>

				{publisherId && (
					<>
						<div className="flex justify-end">
							<button
								type="button"
								onClick={() =>
									exportToExcel(
										{
											fromDate: filters.fromDate,
											toDate: filters.toDate,
											revenueLine: filters.revenueLine,
											categoryIs: filters.categoryIs,
											searchTerm: filters.searchTerm,
										},
										publisherId,
									)
								}
								className="h-10 px-4 bg-akgreener rounded-lg text-white font-semibold flex items-center gap-2 hover:bg-akgreenest transition-colors"
							>
								<FiDownload size={18} />
								<span>Export to Excel</span>
							</button>
						</div>

						<Formik
							key={publisherId}
							enableReinitialize
							initialValues={initialValues}
							onSubmit={handleSubmit}
						>
							{({ values, setFieldValue }) => {
								const handleClearFilters = () => {
									setFieldValue("rowsPerPage", 50);
									setFieldValue("page", "");
									setFieldValue("categoryIs", "");
									setFieldValue("searchTerm", "");
									setFieldValue("fromDate", "");
									setFieldValue("toDate", "");
									setFieldValue("revenueLine", "purchase");

									resetFiltersToDefaults();
								};

								const handleChangeRowsPerPage = (val) => {
									setFieldValue("rowsPerPage", val);
									filters.changeRowsPerPage(val);
								};

								const handleChangeRevenueLine = (value) => {
									setFieldValue("revenueLine", value);
									filters.changeRevenueLine(value);
								};

								return (
									<div className="space-y-4">
										<div className="flex flex-col gap-2">
											<div className="flex flex-wrap items-center justify-between gap-4">
												<h4 className="text-xl font-bold text-gray-800">
													Books sold through{" "}
													<span className="text-akgreener">
														{filters?.revenueLine === "purchase" ? "purchase" : "subscription"}
													</span>{" "}
													model
												</h4>
												<div className="flex gap-2 items-center text-sm text-gray-600">
													<span>Showing</span>
													<select
														name="rowsPerPage"
														className="w-16 h-10 px-2 rounded bg-gray-100 border border-gray-300 font-semibold"
														value={values.rowsPerPage}
														onChange={(e) => handleChangeRowsPerPage(e.target.value)}
														defaultValue={values.rowsPerPage}
													>
														<option value="10">10</option>
														<option value="20">20</option>
														<option value="30">30</option>
														<option value="40">40</option>
														<option value="50">50</option>
													</select>
													<span>books per page</span>
												</div>
											</div>
										</div>

										<div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
											<Form className="flex flex-wrap items-end gap-3">
												<div className="flex items-center rounded-lg overflow-hidden border border-gray-300">
													{[
														{ label: "Purchased", model: "purchase" },
														{ label: "Subscribed", model: "subscription" },
													]?.map((line, index) => (
														<label
															key={index}
															htmlFor={`model_${line.model}`}
															className={`cursor-pointer h-10 px-4 flex items-center justify-center font-semibold text-sm transition-colors ${
																values.revenueLine === line.model
																	? "bg-akgreener text-white"
																	: "bg-white text-gray-600 hover:bg-gray-100"
															}`}
														>
															<span>{line.label}</span>
															<input
																id={`model_${line.model}`}
																type="radio"
																name="revenueLine"
																value={line.model}
																hidden
																onChange={(e) => handleChangeRevenueLine(e.target.value)}
																checked={values.revenueLine === line.model}
															/>
														</label>
													))}
												</div>

												<input
													type="text"
													name="searchTerm"
													value={values.searchTerm}
													onChange={(e) => setFieldValue("searchTerm", e.target.value)}
													className="w-48 h-10 border border-gray-300 rounded-lg px-3 text-sm"
													placeholder="Search author, title..."
												/>

												<select
													name="categoryIs"
													className="w-44 h-10 border border-gray-300 rounded-lg px-3 bg-white text-sm"
													value={values.categoryIs}
													onChange={(e) => setFieldValue("categoryIs", e.target.value)}
												>
													<option label="All Categories"></option>
													<option value="Academic">Academic</option>
													<option value="Non-academic">Non-academic</option>
												</select>

												<Calendar
													value={values.fromDate ? new Date(values.fromDate) : null}
													name="fromDate"
													dateFormat="yy-mm-dd"
													placeholder="From date"
													className="w-40 h-10 font-sans"
													inputClassName="border border-gray-300 bg-white rounded-lg text-sm"
													showIcon
													readOnlyInput
													showButtonBar
													onChange={(e) =>
														setFieldValue(
															"fromDate",
															e.target.value ? moment(e.target.value).format("YYYY-MM-DD") : "",
														)
													}
													onClearButtonClick={() => setFieldValue("fromDate", "")}
												/>

												<Calendar
													value={values.toDate ? new Date(values.toDate) : null}
													name="toDate"
													dateFormat="yy-mm-dd"
													placeholder="To date"
													className="w-40 h-10 font-sans"
													inputClassName="border border-gray-300 bg-white rounded-lg text-sm"
													showIcon
													readOnlyInput
													showButtonBar
													onChange={(e) =>
														setFieldValue(
															"toDate",
															e.target.value ? moment(e.target.value).format("YYYY-MM-DD") : "",
														)
													}
													onClearButtonClick={() => setFieldValue("toDate", "")}
												/>

												<div className="flex items-center gap-2">
													<button
														type="submit"
														className="h-10 px-4 bg-akgreener rounded-lg text-white font-semibold flex items-center gap-2 hover:bg-akgreenest transition-colors"
													>
														<span>Filter</span>
														<BsFilterRight size={18} />
													</button>

													<button
														type="button"
														onClick={handleClearFilters}
														className="h-10 px-4 border border-gray-300 rounded-lg flex items-center gap-2 text-gray-600 hover:bg-gray-100 transition-colors"
													>
														<span>Clear</span>
														<FiX size={18} />
													</button>
												</div>
											</Form>
										</div>
									</div>
								);
							}}
						</Formik>

						<div className="pt-4">
							{isLoadingBooks ? (
								<TableSkeleton label="Loading sold books and sales totals…" columns={[{ label: "Book", kind: "book" }, "Author(s)", "Category", "Copies", "Sales (USD)", "Sales (ZiG)"]} />
							) : (
								<TableView books={books?.data?.records} filters={filters} />
							)}
						</div>

						{!isLoadingBooks && (
							<div className="flex items-center gap-3 mt-4 text-gray-600">
								<span>Total books:</span>
								<span className="text-2xl font-bold text-akgreener">{totalRecords}</span>
							</div>
						)}

						<div className="flex-1 flex items-end pb-4">
							<CustomPaginator
								firstPage={filters.firstPage}
								setFirstPage={filters.changeFirstPage}
								rowsPerPage={filters.rowsPerPage}
								setRowsPerPage={filters.changeRowsPerPage}
								totalRecords={totalRecords}
								setPage={filters.changePage}
								showRowsPerPageDropdown={false}
							/>
						</div>
					</>
				)}
			</div>
		</Admin2>
	);
};

export default PublisherSalesExport;
